import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";

import type { WorkspaceEditorTestFiles } from "../../../types";

/**
 * The strip above a test file saying what the author can do with it: move a
 * generated test into their own file, or start their own file.
 */
export const TestFileBanner = ({
  testFiles,
  readOnly,
}: {
  testFiles: WorkspaceEditorTestFiles;
  readOnly: boolean;
}) => {
  const theme = useTheme();
  const { active, user, generated } = testFiles;
  if (active === null) {
    return null;
  }

  const adopting =
    generated.adoptingTestId === null
      ? null
      : (generated.tests.find((test) => test.id === generated.adoptingTestId)
          ?.title ?? "the test");

  const content =
    active === "generated" ? (
      <IgTypography variant="body2" color="text.secondary">
        {adopting !== null
          ? `Moving “${adopting}” into ${user.name}…`
          : `Edit a test to make it yours: choose “Edit as my test” above it and it moves into ${user.name}.`}
      </IgTypography>
    ) : user.isEmpty ? (
      <>
        <IgTypography variant="body2" color="text.secondary">
          You have no tests of your own yet.
        </IgTypography>
        <IgButton
          size="small"
          variant="outlined"
          disabled={readOnly}
          onClick={user.addTest}
        >
          Add test
        </IgButton>
      </>
    ) : null;

  if (content === null) {
    return null;
  }

  return (
    <Stack
      role="status"
      sx={{ borderBottom: `1px solid ${theme.igBorders.core.default}` }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ minHeight: 36, px: 1.5, py: 0.5 }}
      >
        {content}
      </Stack>
      {adopting !== null && active === "generated" ? (
        <LinearProgress aria-label="Moving test" />
      ) : null}
    </Stack>
  );
};
